package dev.danko.roblox_craft_ai.uikit.i18n

import dev.danko.roblox_craft_ai.shared.model.AssetType

object DeStrings : AppStrings {
    override val common = object : CommonStrings {
        override val cancel = "Abbrechen"
        override val save = "Speichern"
        override val delete = "Löschen"
        override val retry = "Erneut versuchen"
        override val regenerate = "Neu generieren"
        override val close = "Schließen"
        override val clear = "Leeren"
        override val copy = "Kopieren"
        override val copied = "Kopiert"
        override val confirm = "Bestätigen"
        override val send = "Senden"
        override val show = "Anzeigen"
        override val hide = "Ausblenden"
        override val help = "Hilfe"
        override val selectPlaceholder = "Auswählen..."
        override val demoBadge = "Demo"
        override val betaBadge = "Beta"
        override val you = "Du"
        override val language = "Sprache"
        override val simpleMode = "Einfacher Modus"
        override val expertMode = "Expertenmodus"
        override val simpleModeHint = "Der einfache Modus blendet die Auswahl des KI-Modells und technische Details aus"
        override fun lines(count: Int) = if (count == 1) "1 Zeile" else "$count Zeilen"
        override fun secondsShort(seconds: Int) = "${seconds}s"
        override fun minutesSeconds(minutes: Int, seconds: Int) = "${minutes}m ${seconds.toString().padStart(2, '0')}s"
        override fun agoSeconds(seconds: Int) = "vor ${seconds}s"
        override fun agoMinutes(minutes: Int) = "vor ${minutes}m"
    }

    override val assetTypes = object : AssetTypeStrings {
        override fun name(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Luau-Skript"
            AssetType.MESH_3D -> "3D-Modell"
            AssetType.ANIMATION -> "Animation"
            AssetType.ROJO_PROJECT -> "Rojo-Projekt"
        }

        override fun generateButton(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Skript erstellen"
            AssetType.MESH_3D -> "3D-Modell erstellen"
            AssetType.ANIMATION -> "Animation erstellen"
            AssetType.ROJO_PROJECT -> "Rojo-Projekt erstellen"
        }

        override fun availabilityNote(type: AssetType) = when (type) {
            AssetType.MESH_3D -> "Beta: Das 3D-Modell entsteht aus einer Skizze oder deinem Bild (Büroklammer). In Studio wird das Modell vorerst als Platzhalter eingefügt."
            AssetType.ANIMATION -> "Beta: Die KI erstellt eine Animation für eine R15-Figur. Wähle vor dem Senden an Studio ein R15-Rig aus — sie öffnet sich im Animation Editor."
            else -> null
        }
    }

    override val errors = object : ErrorStrings {
        override val aiUnavailable = "Die KI ist gerade nicht erreichbar. Versuch es in einer Minute noch einmal."
        override val rateLimited = "Zu viele Anfragen. Warte kurz und versuch es noch einmal."
        override val network = "Keine Verbindung zum Server. Prüfe, ob das Backend läuft."
        override val meshTimeout = "Das 3D-Modell hat zu lange gebraucht. Versuch es noch einmal."
        override val meshFailed = "Das 3D-Modell konnte nicht erstellt werden. Beschreib es mal anders."
        override val cancelled = "Generierung abgebrochen."
        override val generic = "Etwas ist schiefgelaufen. Versuch es noch einmal."
        override val details = "Details"
        override val backendDownTitle = "Server läuft nicht"
        override val backendDownHint = "Solange das Backend aus ist, kann nichts erstellt werden. Starte es im Projektordner mit ./gradlew :backend:run — die Seite merkt automatisch, wenn es läuft."
        override val checkAgain = "Erneut prüfen"
        override val contentBlocked = "Diese Anfrage geht nicht: Sie verstößt gegen die Regeln für Roblox-Spiele. Beschreib deine Idee mal anders."
        override val unsafeCodeBlocked = "Die KI hat Code geschrieben, der gegen die Sicherheitsregeln verstößt, deshalb haben wir ihn nicht ausgegeben. Formuliere deine Anfrage um."
        override val authRequired = "Melde dich mit deiner E-Mail an, um etwas zu erstellen — das dauert nur eine Minute."
        override val quotaExceeded = "Deine Credits sind aufgebraucht. Kauf ein Credit-Paket oder wähl einen Tarif auf der Seite „💎 Tarife“."
        override val budgetExceeded = "Das Generierungslimit für heute ist erreicht. Versuch es morgen wieder."
    }

    override val promptBox = object : PromptBoxStrings {
        override val label = "Was bauen wir?"
        override val attachReference = "Bild anhängen"
        override fun addMorePhotos(count: Int) = "Bild hinzufügen ($count)"
        override val placeholder = "Beschreib, was du für Roblox brauchst. Du kannst auch ein Bild einfügen (Strg+V / Cmd+V)..."
        override val referencesLabel = "🖼️ Beispielbilder:"
        override val assetTypeLabel = "Was erstellen wir"
        override val aiModelLabel = "KI-Modell"
        override val autoProvider = "🤖 Auto"
        override val generating = "Wird erstellt..."
        override val tooManyReferences = "⚠️ Höchstens drei Bilder. Die übrigen wurden nicht hinzugefügt"
        override val viewsTitle = "Ansichten"
        override val viewFront = "Vorne"
        override val viewBack = "Hinten"
        override val viewLeft = "Links"
        override val viewRight = "Rechts"
        override val viewsHint = "Lade ein Blatt mit mehreren Ansichten hoch, es wird automatisch zerschnitten, oder füge Ansichten einzeln hinzu. Ab 2 Ansichten entsteht das Modell direkt daraus, ohne Skizze."
        override val viewsSplitting = "Suche Ansichten im Bild…"
        override fun viewsSplit(count: Int) = "$count Ansichten gefunden. Prüfe, ob alle am richtigen Platz sind"
        override val viewsSwapSides = "Links und rechts tauschen"
        override val viewsHighOnlyFront = "Hoch (TRELLIS 2) nutzt nur die Vorderansicht. Normal und Maximal nutzen alle Ansichten."
        override val viewsSwitchToMax = "Auf Maximal wechseln"
        override val viewsTooMany = "⚠️ Die Plätze sind voll. Entferne ein Bild, um ein neues hinzuzufügen"
        override val onlyImages = "⚠️ Nur PNG, JPG und WebP sind erlaubt"
        override val fileTooLarge = "⚠️ Datei zu groß, maximal 5 MB"
        override val dropTitle = "+ Beispielbilder hierher ziehen"
        override val dropHint = "Bis zu 3 Dateien, je bis 5 MB"
        override val dropAria = "Beispielbilder hierher ziehen, höchstens drei"
        override val emptyPrompt = "✏️ Beschreib zuerst, was du erstellen willst"
        override fun promptFromImages(type: AssetType) = "${assetTypes.name(type)} für Roblox nach den angehängten Bildern"
    }

    override val assetCard = object : AssetCardStrings {
        override val stagePrompt = "Anfrage"
        override val stageAi = "KI"
        override val stageBuild = "Aufbau"
        override val stageSync = "Prüfung"
        override val stageDone = "Fertig"
        override val statusPending = "Wartet"
        override val statusValidating = "Prüfung"
        override val statusRetrying = "Korrektur"
        override val statusStreaming = "Wird erstellt"
        override val statusReady = "Fertig"
        override val statusFailed = "Fehler"
        override fun revisionBadge(current: Int, total: Int) = "v$current ($total ${if (total == 1) "Version" else "Versionen"})"
        override fun referencesBadge(count: Int) = "🖼️ $count"
        override fun syncPending(countdown: String) = "🔗 Warte auf Studio...$countdown"
        override fun syncDelivering(countdown: String) = "⚙ Wird gesendet...$countdown"
        override val syncSynced = "✅ Schon in Studio!"
        override val syncFailed = "❌ Senden fehlgeschlagen"
        override val deleteTooltip = "Löschen"
        override val waitingForPlugin = "🔗 Warte darauf, dass das Roblox-Studio-Plugin den Auftrag abholt..."
        override val progressValidating = "⚙ Prüfe den Code..."
        override val progressRetrying = "⚠️ Fehler gefunden, korrigiere..."
        override fun progressTokens(count: Int) = "⚡ Schreibe Code... ($count)"
        override val progressProcessing = "⏳ Arbeite..."
        override val download = "📦 Herunterladen: "
        override val editPrompt = "✏️ Anfrage ändern"
        override val aiChat = "KI-Chat"
        override val downloadZip = ".ZIP herunterladen"
        override val synced = "Gesendet!"
        override val retrySync = "Erneut senden"
        override val syncToStudio = "In Roblox Studio"
        override val demoMeshHint = "Das ist ein Demo-Modell: Ein echtes 3D-Modell entsteht aus einer Skizze oder einem Bild, sobald auf dem Server ein 3D-Generator eingerichtet ist."
        override val betaAnimationHint = "Animationen sind in der Beta: Das ist ein Demo-Ergebnis."
    }

    override val pipeline = object : PipelineStrings {
        override val prompt = "Anfrage"
        override val luauPromptDescription = "Anfrage angenommen"
        override val llmStream = "KI schreibt Code"
        override val llmStreamDescription = "Das neuronale Netz schreibt Luau-Code"
        override val syntaxCheck = "Prüfung"
        override val syntaxCheckDescription = "Syntax- und Sicherheitsprüfung des Codes"
        override val ready = "Fertig"
        override val luauReadyDescription = "Das Skript kann an Studio gesendet werden"
        override val meshPromptDescription = "Beschreibung des 3D-Modells"
        override val meshGeneration = "3D-Generierung"
        override val meshGenerationDescription = "Form des Modells wird erstellt"
        override val meshTexturing = "Texturen"
        override val meshTexturingDescription = "Farben und Materialien"
        override val meshExport = "GLB-Export"
        override val meshExportDescription = "Modelldatei wird vorbereitet"
        override val meshReadyDescription = "3D-Modell ist fertig"
        override val animationPromptDescription = "Beschreibung der Animation"
        override val motionAi = "Bewegung"
        override val motionAiDescription = "Schlüsselbilder werden erstellt"
        override val rigging = "R15-Skelett"
        override val riggingDescription = "Bindung an die R15-Knochen"
        override val animationReadyDescription = "Animation ist fertig (Beta)"
        override val scaffoldTree = "Struktur"
        override val scaffoldTreeDescription = "Dateien des Rojo-Projekts werden erstellt"
        override val serverServices = "Server"
        override val serverServicesDescription = "Server-Services"
        override val clientControllers = "Client"
        override val clientControllersDescription = "Client-Controller und UI"
        override val rojoReady = "Fertig"
        override val rojoReadyDescription = "Projekt ist bereit für Rojo"
    }

    override val chat = object : ChatStrings {
        override val quickOptimize = "⚡ Code beschleunigen"
        override val quickStrict = "🛡️ --!strict und Typen aktivieren"
        override val quickBugs = "🐛 Fehler finden"
        override val quickErrors = "✨ Fehlerbehandlung hinzufügen"
        override val quickDocs = "📝 Kommentare hinzufügen"
        override fun revisionShort(number: Int) = "Version $number"
        override fun revisionLong(number: Int) = "Version Nr. $number"
        override val inputPlaceholder = "Schreib, was am Skript geändert oder verbessert werden soll."
        override val aiAssistant = "🤖 KI-Assistent"
        override val diffTitle = "Änderungen (Diff)"
        override val acceptChanges = "Änderungen übernehmen"
        override val thinking = "Die KI liest den Code und nimmt Änderungen vor..."
        override val changesApplied = "✅ Änderungen übernommen"
    }

    override val code = object : CodeStrings {
        override val run = "Ausführen"
        override val runViaBackend = "Über das Backend ausführen…"
        override val connectPluginFirst = "Verbinde zuerst das Roblox-Studio-Plugin"
        override fun projectTree(files: Int) = "📁 Projektdateien ($files)"
        override val filterPlaceholder = "Filter..."
        override val undoTooltip = "Letzte Aktion rückgängig machen"
        override val redoTooltip = "Rückgängig gemachte Aktion wiederholen"
        override val resetTooltip = "Ursprünglichen Code wiederherstellen"
        override val copyTooltip = "Code kopieren"
        override val downloadTooltip = "Skript als .lua herunterladen"
        override val fullscreenTooltip = "Vollbild"
    }

    override val meshViewer = object : MeshViewerStrings {
        override val shadingTextured = "Texturen"
        override val shadingClay = "Ton"
        override val shadingWire = "Gitter"
        override val shadingFlat = "Flach"
        override val lightStudio = "Studio"
        override val lightSun = "Sonne"
        override val lightNeon = "Neon"
        override val cameraIso = "Iso"
        override val cameraFront = "Vorne"
        override val cameraTop = "Oben"
        override val cameraSide = "Seite"
        override val pause = "Pause"
        override val spin = "Drehen"
        override val exitFullscreen = "Beenden"
        override val fullscreen = "Vollbild"
        override fun stats(triangles: String, vertices: String, dimensions: String) = "📊 $triangles Dreiecke • $vertices Ecken • $dimensions"
        override val controlsHint = "Linke Maustaste: drehen • Rechte/Shift: verschieben • Mausrad: zoomen"
        override val screenshot = "Screenshot"
        override val screenshotTitle = "3D-Screenshot"
        override fun screenshotSaved(filename: String) = "Screenshot des Modells gespeichert: $filename"
        override val animationParseError = "Animation konnte nicht gelesen werden, die Figur bleibt in der Grundpose"
        override val animationMissing = "Diese Arbeit hat keine Keyframes (alte Demo) — erstelle die Animation neu"
        override val animationPreviewTitle = "Animationsvorschau"
        override val play = "Abspielen"
        override val stop = "Stopp"
        override val loopOn = "Wiederholen: AN"
        override val loopOff = "Wiederholen: AUS"
        override val exportKeyframes = "Schlüsselbilder exportieren (.json)"
        override fun resizeHint(heightPx: Int) = "↕ Ziehen, um die Höhe zu ändern (${heightPx}px)"
        override val shadingBaseColor = "Nur Farbe"
        override val shadingNormals = "Normalen"
        override val shadingUvChecker = "UV-Raster"
        override val shadingXray = "Röntgen"
        override val lightRoblox = "Roblox"
        override val lightNight = "Nacht"
    }

    override val header = object : HeaderStrings {
        override val appTitle = "Craftiva AI"
        override val appSubtitle = "Roblox-Werkstatt"
        override val presets = "📚 Ideen"
        override val settings = "Einstellungen"
        override fun aiReady(count: Int) = "KI: $count verbunden"
        override val aiDemo = "KI: Demo-Modus"
        override val pluginConnected = "Plugin: verbunden"
        override val pluginPending = "Plugin: wartet"
        override val tokenLabel = "Token: "
    }

    override val home = object : HomeStrings {
        override val searchWorks = "Werk suchen"
        override val allIdeas = "Alle Ideen →"
        override val open = "Öffnen"
        override val moreActions = "Weitere Aktionen"
        override val generateSimilar = "Ähnliches"
        override val options = "Optionen"
        override val sendToStudio = "An Roblox Studio senden"
        override fun creditsCost(credits: Int) = if (credits == 1) "1 Credit" else "$credits Credits"
        override val rename = "Umbenennen"
        override val renameFailed = "Umbenennen fehlgeschlagen"
        override val noMatches = "Nichts gefunden"
        override val pageTitle = "Craftiva AI — Roblox-Werkstatt"
        override val heroTitle = "Was bauen wir heute?"
        override val heroSubtitle = "Beschreib deine Idee — du bekommst ein fertiges Skript oder Modell und schickst es mit einem Klick an Roblox Studio."
        override val samplePrompt = "Mach ein Holzschwert"
        override val savePreset = "💾 Als Idee speichern"
        override val addToQueue = "✚ In die Warteschlange"
        override val conceptMode = " 🎨 Erst Skizze, dann 3D"
        override val shiftHint = "💡 Shift + „Erstellen“ = in die Warteschlange"
        override val noIdea = "💡 Keine Idee, was du bauen sollst?"
        override val browsePresets = "📚 Ideen ansehen"
        override val recentAssets = "Meine Werke"
        override fun totalAssets(count: Int) = "(insgesamt $count)"
        override val wideView = "Breit"
        override val gridView = "Raster"
        override val noAssetsTitle = "Noch leer"
        override val noAssetsHint = "Schreib oben eine Anfrage, um dein erstes Werk zu erstellen."
        override fun generationFailed(reason: String) = "Generierung fehlgeschlagen: $reason"
        override val cancelledByUser = "Generierung abgebrochen"
        override val conceptGenerating = "🎨 Zeichne eine Skizze..."
        override val conceptReady = "✨ Skizze fertig! Prüf die Form vor dem 3D-Schritt."
        override fun conceptFailed(reason: String) = "Skizze fehlgeschlagen: $reason"
        override val conceptApproved = "🚀 Skizze bestätigt! Erstelle das 3D-Modell..."
        override fun meshFromConceptFailed(reason: String) = "3D-Modell fehlgeschlagen: $reason"
        override val conceptRerolling = "🔄 Zeichne eine andere Variante..."
        override val conceptRerolled = "✨ Neue Variante fertig!"
        override fun conceptRerollFailed(reason: String) = "Neu zeichnen fehlgeschlagen: $reason"
        override fun presetLoaded(title: String) = "✨ Idee „$title“ geladen"
        override fun queued(pending: Int, position: Int) = "✨ In der Warteschlange: $pending, Position Nr. $position"
        override val enterPromptBeforeSaving = "⚠️ Schreib zuerst eine Anfrage"
        override val personalPresetDescription = "Meine Idee"
        override fun presetSaved(title: String) = "💾 Idee „$title“ gespeichert. Du findest sie unter 📚 Ideen → 👤 Meine"
        override val presetSaveFailed = "Speichern fehlgeschlagen"
        override fun cardDeleted(shortId: String) = "🗑️ Werk #$shortId gelöscht"
        override val regenerating = "🔄 Erstelle neu..."
        override fun queuedForStudio(shortId: String) = "Werk #$shortId wurde in die Studio-Warteschlange gestellt!"
        override val syncBackendUnavailable = "Der Server zum Senden ist nicht erreichbar."
        override val settingsSaved = "Einstellungen gespeichert!"
        override fun refactorFailed(reason: String) = "Fehler beim Ändern des Codes: $reason"
        override val changesApplied = "Änderungen übernommen!"
        override fun applyChangesFailed(reason: String) = "Änderungen konnten nicht übernommen werden: $reason"
    }

    override val presetDialog = object : PresetDialogStrings {
        override val title = "💾 Als eigene Idee speichern"
        override val nameLabel = "Name"
        override val namePlaceholder = "Mein cooler Feuereffekt (3–60 Zeichen)"
        override val nameTooShort = "Mindestens 3 Zeichen"
        override fun nameCounter(length: Int) = "OK · $length/60"
        override val descriptionLabel = "Beschreibung (optional)"
        override val descriptionPlaceholder = "Kurz, wofür diese Anfrage ist (bis 140 Zeichen)"
        override val tagsLabel = "Tags (optional)"
        override val tagsPlaceholder = "feuer, magie, schwert — durch Kommas getrennt"
        override fun tagsCounter(count: Int) = "Bis zu 6 Tags · $count"
        override val currentPrompt = "Aktuelle Anfrage (wird gespeichert):"
        override val emptyPrompt = "(leer — schreib zuerst eine Anfrage)"
        override fun typeAndLimit(typeName: String) = "Typ: $typeName · höchstens 50 eigene Ideen"
    }

    override val queue = object : QueueStrings {
        override val statusPending = "Wartet"
        override val statusRunning = "Läuft"
        override val statusDone = "Fertig"
        override val statusFailed = "Fehler"
        override val statusCancelled = "Abgebrochen"
        override fun title(size: Int) = "📦 Warteschlange ($size)"
        override fun eta(minutes: Int, seconds: Int) = " ⏱️ Noch ~${minutes}m ${seconds.toString().padStart(2, '0')}s"
        override val resume = "▶️ Fortsetzen"
        override val pause = "⏸️ Pause"
        override val resumed = "Warteschlange fortgesetzt"
        override val paused = "Warteschlange pausiert"
        override val pausedBadge = " ⏸️ Pausiert"
        override val cleared = "Fertige Aufgaben entfernt"
        override val empty = "🕳️ Noch keine Aufgaben. Shift + „Erstellen“ fügt eine Aufgabe zur Warteschlange hinzu."
        override val scrollTip = "💡 Ab 30 Aufgaben lässt sich die Liste scrollen"
        override val taskCancelled = "Aufgabe abgebrochen"
        override fun position(position: Int) = "Position $position"
        override val startCallbackMissing = "Die Warteschlange ist noch nicht bereit"
        override val generationFailed = "Generierung fehlgeschlagen"
        override val unexpectedError = "Unerwarteter Fehler"
    }

    override val concept = object : ConceptStrings {
        override val stepTitle = "1. Skizze (2D)"
        override val stepHint = "Prüf Form und Silhouette, bevor das 3D-Modell erstellt wird"
        override val savingBadge = "💎 Spart 3D-Generierungen"
        override val imageAlt = "Skizze"
        override fun promptExpansion(expanded: Boolean) = "🔍 Ausführliche Anfrage an den Zeichner (${if (expanded) "einklappen" else "anzeigen"})"
        override val reconstructing = "⏳ Erstelle 3D..."
        override val approve = "✅ Gefällt mir, mach 3D"
        override val regenerating = "↻ Zeichne..."
        override val regenerate = "🔄 Andere zeichnen"
    }

    override val health = object : HealthStrings {
        override val unknown = "⚪ Status unbekannt"
        override val allOk = "🌿 Alle Systeme in Ordnung"
        override val critical = "🔴 Kritischer Fehler"
        override fun degraded(component: String) = "🟡 Probleme: $component"
        override val degradedGeneric = "🟡 Es gibt Probleme"
        override val title = "Systemstatus"
        override fun updatedAt(time: String) = "Aktualisiert: $time"
    }

    override val sessionToken = object : SessionTokenStrings {
        override val validationFailed = "Der Token konnte nicht geprüft werden. Stell sicher, dass er mit dem Token im Roblox-Studio-Plugin übereinstimmt."
        override val title = "🔗 Roblox-Studio-Plugin verbinden"
        override val saveAndLink = "Speichern und verbinden"
        override val instructions = "💡 Kopiere den Token dieser Seite und füge ihn im Craftiva-Plugin-Fenster in Roblox Studio ein — dann landen deine Werke direkt in Studio."
        override val tokenLabel = "Sitzungs-Token"
        override val tokenPlaceholder = "z. B. RK_LIVE_7AB3C9EF"
        override val pasteFromClipboard = "Aus Zwischenablage einfügen"
        override val generateNew = "Neuen erstellen"
    }

    override val syncModal = object : SyncModalStrings {
        override val untitledAsset = "(ohne Namen)"
        override val title = "⚠️ An Roblox Studio senden?"
        override val confirm = "Ja, senden"
        override val warning = "⚠️ Das Werk erscheint bei der nächsten Plugin-Synchronisierung (alle 3 Sekunden) in deinem Roblox Studio. Skripte werden deaktiviert eingefügt — prüf den Code und klick im Plugin-Fenster auf „Aktivieren“. Mit Strg+Z lässt sich alles rückgängig machen."
        override val averageDelay = "⏱️ Das dauert meist 2–3 Sekunden."
        override val dontShowAgain = "Nicht mehr fragen (nicht empfohlen)"
    }

    override val revisions = object : RevisionStrings {
        override val title = "👁️ Code und Versionsverlauf"
        override val listLabel = "Versionen:"
        override fun restore(number: Int) = "v$number wiederherstellen"
        override fun restored(number: Int) = "Version v$number wiederhergestellt"
        override fun latest(number: Int) = "Neueste (v$number)"
    }

    override val settingsRedirect = object : SettingsRedirectStrings {
        override val deprecatedNotice = "Nutze statt dieses Fensters die Seite /settings."
        override val redirecting = "Weiterleitung..."
        override val openingNewPage = "Die Einstellungsseite wird geöffnet"
        override val movedNotice = "Die Einstellungen sind auf eine eigene Seite /settings umgezogen"
        override val openSettings = "Einstellungen öffnen →"
    }

    override val settingsPage = object : SettingsPageStrings {
        override val tabAccount = "👤 Konto"
        override val signedOutTitle = "Du bist nicht angemeldet"
        override val signedOutHint = "Melde dich mit deiner E-Mail an, um Tarif und Credits zu sehen und den Dienst auf jedem Gerät zu nutzen."
        override val advanced = "Erweitert"
        override val dangerZone = "Zurücksetzen"
        override val resetSessionHint = "Löscht alle Schlüssel und Einstellungen dieser Sitzung. Werke auf dem Server bleiben erhalten."
        override val pageTitle = "Craftiva AI — Einstellungen"
        override val subtitle = "Einstellungen"
        override val hashLabel = "Hash: "
        override val backToStudio = "← Zur Werkstatt"
        override val cabinetTitle = "⚙️ Einstellungen"
        override val cabinetSubtitle = "Konto, Roblox-Studio-Plugin und Einstellungen"
        override val resetSession = "Sitzung zurücksetzen"
        override val resetSessionConfirm = "Alle Schlüssel und Einstellungen dieser Sitzung löschen?"
        override val sessionReset = "Sitzung zurückgesetzt"
        override val tabKeys = "🔑 API-Schlüssel"
        override val tabGeneral = "⚙️ Allgemein"
        override val tabPlugin = "🔌 Plugin"
        override val tabAssets = "📦 Werke"
        override val keyAdded = "Schlüssel hinzugefügt ✓"
        override val keyAddFailed = "Schlüssel konnte nicht hinzugefügt werden"
        override val keyUpdated = "Schlüssel aktualisiert"
        override val keyUpdateFailed = "Schlüssel konnte nicht aktualisiert werden"
        override val keyDeleted = "Schlüssel gelöscht"
        override val keyDeleteFailed = "Schlüssel konnte nicht gelöscht werden"
        override val priorityChanged = "Priorität geändert"
        override val priorityChangeFailed = "Priorität konnte nicht geändert werden"
        override val settingsSaved = "Einstellungen gespeichert ✓"
        override val saveFailed = "Speichern fehlgeschlagen"
        override val resetDone = "Einstellungen zurückgesetzt"
        override val resetFailed = "Zurücksetzen fehlgeschlagen"
        override val pluginSettingsSaved = "Plugin-Einstellungen gespeichert"
        override val connectionOkEmpty = "Verbindung steht (Warteschlange leer)"
        override fun connectionOkQueued(count: Int) = "Verbindung steht (in der Warteschlange: $count)"
        override val connectionFailed = "Der Synchronisierungsserver ist nicht erreichbar"
        override val assetRemovedFromCache = "Werk aus dem Browser-Cache entfernt"
        override val keysTitle = "API-Schlüssel verwalten"
        override val keysManagedByServerTitle = "🔐 Schlüssel werden auf dem Server verwaltet"
        override val keysManagedByServerHint = "Die Anbieter-Schlüssel legt der Server-Administrator über Umgebungsvariablen oder Secrets fest. In der Oberfläche lassen sie sich nicht ändern."
        override fun keysSummary(total: Int, active: Int) = "Schlüssel insgesamt: $total · Aktiv: $active"
        override val refresh = "Aktualisieren"
        override val catalogMissing = "Anbieterkatalog nicht geladen"
        override val catalogMissingHint = "Prüfe, ob das Backend läuft und erreichbar ist"
        override val providersHeader = "🤖 KI- und 3D-Anbieter"
        override val noModels = "(keine Modelle verfügbar — füge einen Schlüssel hinzu)"
        override val generalTitle = "Allgemeine Einstellungen"
        override val defaultModel = "Standard-KI-Modell"
        override val selectModel = "Modell auswählen..."
        override val defaultAssetType = "Standard-Werktyp"
        override val skipSyncConfirm = "Vor dem Senden an Studio nicht fragen"
        override val skipSyncConfirmHint = "Fertige Werke werden sofort an Roblox Studio gesendet"
        override val themeSystem = "🌓 Wie im System"
        override val themeLight = "☀️ Hell"
        override val themeDark = "🌙 Dunkel"
        override val themeLabel = "Design"
        override val languageLabel = "Sprache der Oberfläche"
        override val reset = "Zurücksetzen"
        override val pluginSettingsTitle = "Einstellungen des Roblox-Studio-Plugins"
        override val pluginTokenTitle = "Sitzungs-Token für das Plugin"
        override val tokenCopied = "Token kopiert"
        override val sessionHashTitle = "Sitzungs-Hash (Speicher-ID im Backend)"
        override val pluginPort = "Plugin-Port"
        override val pluginPortHint = "Die Serveradresse wird im Plugin-Fenster in Roblox Studio eingestellt"
        override val autoConnect = "Beim Start automatisch verbinden"
        override val autoConnectHint = "Das Plugin startet die Synchronisierung direkt nach dem Öffnen von Studio"
        override val studioPath = "Installationspfad von Roblox Studio (optional)"
        override val testConnection = "Verbindung testen"
        override val howToConnectTitle = "📚 Wie verbinde ich das Plugin?"
        override val howToConnectSteps = listOf(
            "1. Lade das Plugin mit dem Knopf unten herunter — die Serveradresse ist bereits eingetragen.",
            "2. Lege die Datei in den Plugin-Ordner von Roblox Studio (in Studio: Plugins → Plugins Folder) und starte Studio neu.",
            "3. Öffne das Craftiva-AI-Panel, füge oben den Sitzungstoken ein und klicke „Speichern und verbinden“. Erlaube Studio den Zugriff auf den Server, wenn gefragt.",
            "4. Fertige Arbeiten kommen deaktiviert in Studio an: Prüfe den Code und klicke „Aktivieren“. Für Animationen zuerst ein R15-Rig auswählen. Strg+Z macht das Einfügen rückgängig."
        )
        override val downloadPlugin = "Plugin herunterladen"
        override val myAssetsTitle = "Meine Werke (Browser-Cache)"
        override fun assetsSummary(total: Int, shown: Int) = "Insgesamt: $total · Angezeigt: $shown"
        override val filterAll = "Alle"
        override val filterReady = "Fertig"
        override val filterProcessing = "In Arbeit"
        override val filterFailed = "Fehler"
        override val deselectAll = "Auswahl aufheben"
        override val selectAll = "Alle auswählen"
        override fun export(selected: Int) = if (selected > 0) "Exportieren ($selected)" else "Alle exportieren"
        override fun deleteSelected(count: Int) = "Löschen ($count)"
        override fun deleteAssetsConfirm(count: Int) = "$count Werke aus dem Cache löschen?"
        override val deleted = "Gelöscht"
        override val noAssets = "Noch keine Werke"
        override val noAssetsHint = "Erstelle dein erstes Werk auf der Startseite"
        override val goToStudio = "Zur Werkstatt →"
    }

    override val providerCard = object : ProviderCardStrings {
        override val defaultLabel = "Standard"
        override fun type(name: String) = "Typ: $name"
        override val active = "✓ Aktiv"
        override val notConfigured = "Nicht eingerichtet"
        override fun addKeyFor(provider: String) = "+ Schlüssel für $provider hinzufügen"
        override fun addKeyTitle(provider: String) = "$provider-Schlüssel hinzufügen"
        override val labelField = "Name des Schlüssels"
        override val labelPlaceholder = "Standard, Arbeit usw."
        override val apiKeyField = "API-Schlüssel"
        override val apiKeyPlaceholder = "API-Schlüssel einfügen..."
        override fun keyPatternWarning(provider: String) = "⚠️ Ein $provider-Schlüssel sieht normalerweise anders aus"
        override val keyTooShort = "Schlüssel zu kurz (mindestens 8 Zeichen)"
        override val baseUrlOptional = "Base URL (optional)"
        override fun baseUrlDefault(url: String) = "Standard: $url"
        override val test = "Testen"
        override val getApiKey = "API-Schlüssel holen ↗"
        override fun editKeyTitle(provider: String) = "$provider-Schlüssel bearbeiten"
        override val labelShort = "Name"
        override val baseUrlEmptyDefault = "Base URL (leer = Standard)"
        override val keyHiddenNotice = "🔒 Der Schlüssel ist gespeichert und wird nicht angezeigt. Um ihn zu ersetzen, leg einen neuen Schlüssel an."
        override fun deleteKeyTitle(label: String) = "Schlüssel „$label“ löschen?"
        override val deleteIrreversible = "Das lässt sich nicht rückgängig machen. Der Schlüssel wird dauerhaft aus den Einstellungen entfernt."
        override fun providerKeyLine(provider: String, key: String) = "⚠️ Anbieter: $provider · Schlüssel: $key"
        override fun secondsLeft(seconds: Int) = "(${seconds}s)"
    }

    override val presetsPage = object : PresetsPageStrings {
        override val pageTitle = "Craftiva AI — Ideen"
        override val subtitle = "📚 Ideenbibliothek"
        override val toStudio = "🎮 Zur Werkstatt"
        override val categories = "Kategorien"
        override val categoryAll = "Alle"
        override val categoryEnvironment = "Umgebung"
        override val categoryNpc = "Figuren und NPCs"
        override val categoryInterior = "Einrichtung und Möbel"
        override val categoryWeapon = "Waffen und Werkzeuge"
        override val categoryItem = "Gegenstände"
        override val categoryAnimation = "Animationen und Effekte"
        override val categoryScript = "Skripte"
        override val categoryPersonal = "Meine"
        override val categoryOther = "Sonstiges"
        override val allCategories = "Alle Kategorien"
        override val difficultyEasy = "Leicht"
        override val difficultyMedium = "Mittel"
        override val difficultyHard = "Schwer"
        override val searchPlaceholder = "🔍 Nach Name, Beschreibung, Tags suchen..."
        override fun found(count: Int) = "Gefunden: $count"
        override val noPersonalTitle = "Du hast noch keine eigenen Ideen"
        override val noPersonalHint = "Speichere eine gelungene Anfrage auf der Startseite — mit „💾 Als Idee speichern“."
        override val goToStudio = "🎮 Zur Werkstatt"
        override fun nothingFound(query: String) = "Nichts gefunden für „$query“"
        override val nothingFoundHint = "Versuch eine andere Suche oder wähl eine andere Kategorie."
        override val deleteTitle = "Idee löschen?"
        override fun deleted(title: String) = "Idee „$title“ gelöscht"
        override fun deleteConfirm(title: String) = "Die Idee „$title“ wird endgültig gelöscht. Fortfahren?"
        override val usePrompt = "Verwenden"
    }

    override val nav = object : NavStrings {
        override val menu = "Menü"
        override val on = "An"
        override val off = "Aus"
        override fun plan(name: String) = "Tarif $name"
        override val studioConnected = "Roblox-Studio-Plugin verbunden"
        override val studioNotConnected = "Roblox-Studio-Plugin nicht verbunden"
        override val workshop = "🎮 Werkstatt"
        override val home = "Start"
        override val about = "Über das Projekt"
        override val toggleColorMode = "Design wechseln"
        override val builtWith = "Erstellt mit "
        override val templateBy = ", Vorlage von "
    }

    override val report = object : ReportStrings {
        override val button = "Melden"
        override val title = "🚩 Werk melden"
        override val hint = "Erzähl uns, was nicht stimmt. Ein Administrator prüft die Meldung."
        override val reasonLabel = "Grund"
        override val reasonInappropriate = "Unangemessener Inhalt"
        override val reasonUnsafeCode = "Gefährlicher oder schädlicher Code"
        override val reasonCopyright = "Urheberrechtsverletzung"
        override val reasonOther = "Sonstiges"
        override val commentLabel = "Kommentar (optional)"
        override val commentPlaceholder = "Was genau stimmt nicht?"
        override val submit = "Meldung senden"
        override val thanks = "Danke! Deine Meldung wurde gesendet."
        override val failed = "Die Meldung konnte nicht gesendet werden. Versuch es später noch einmal."
    }

    override val auth = object : AuthStrings {
        override val signIn = "Anmelden"
        override val signOut = "Abmelden"
        override val signedOut = "Du hast dich abgemeldet"
        override val accountTooltip = "Konto"
        override val dialogTitle = "Mit E-Mail anmelden"
        override val dialogHint = "Wir schicken dir einen Anmeldelink per E-Mail. Kein Passwort nötig."
        override val emailLabel = "E-Mail"
        override val emailPlaceholder = "du@example.com"
        override val sendLink = "Link senden"
        override val sentTitle = "📬 Schau in dein Postfach"
        override fun sentHint(email: String) = "Wir haben einen Link an $email geschickt. Er gilt 15 Minuten. Wenn du ihn nicht findest, schau im Spam-Ordner nach."
        override val useAnotherEmail = "Andere E-Mail"
        override val invalidEmail = "Prüf die Adresse: Da scheint ein Tippfehler zu sein."
        override val throttled = "Zu viele E-Mails. Warte 15 Minuten und versuch es noch einmal."
        override val sendFailed = "Die E-Mail konnte nicht gesendet werden. Versuch es später noch einmal."
        override val verifyTitle = "Anmeldung bei Craftiva AI"
        override val verifying = "Link wird geprüft…"
        override fun verifySuccess(email: String) = "Fertig! Du bist als $email angemeldet."
        override val verifyFailed = "Dieser Link funktioniert nicht: Er ist abgelaufen oder wurde schon benutzt."
        override val requestNewLink = "Neuen Link anfordern"
        override val toWorkshop = "Zur Werkstatt →"
        override fun creditsTooltip(left: Int, limit: Int, purchased: Int, resetsOn: String) = "Monatlich: $left von $limit (Erneuerung am $resetsOn). Gekauft: $purchased"
    }

    override val billing = object : BillingStrings {
        override val navPricing = "💎 Tarife"
        override val pageTitle = "Craftiva AI — Tarife"
        override val title = "Tarife und Credits"
        override val subtitle = "Jede Aktion kostet Credits: ein Skript 1, ein 3D-Modell 10. Monatliche Credits werden jeden Monat erneuert, gekaufte verfallen nie."
        override val perMonth = "/Monat"
        override fun creditsPerMonth(credits: Int) = "$credits Credits pro Monat"
        override val unlimitedCredits = "Unbegrenzt"
        override val currentPlan = "Dein Tarif"
        override val subscribe = "Abonnieren"
        override val switchPlan = "Wechseln"
        override val free = "Kostenlos"
        override val packsTitle = "Credit-Pakete"
        override val packsHint = "Einmalkauf. Die Credits verfallen nicht und werden nach den monatlichen verbraucht."
        override fun packCredits(credits: Int) = "$credits Credits"
        override val buy = "Kaufen"
        override val manageTitle = "Abo verwalten"
        override val cancelSubscription = "Abo kündigen"
        override val updatePayment = "Zahlungsmethode ändern"
        override fun renewsOn(date: String) = "Verlängert sich am $date"
        override val pastDueHint = "Die Zahlung ist fehlgeschlagen. Aktualisiere deine Zahlungsmethode, damit dein Tarif aktiv bleibt."
        override val paymentSoon = "Zahlungen kommen bald"
        override val signInToBuy = "Zum Kaufen anmelden"
        override val checkoutDone = "Zahlung erfolgreich! Tarif und Credits werden in wenigen Sekunden aktualisiert."
        override val checkoutFailed = "Der Bezahlvorgang konnte nicht geöffnet werden. Versuch es später noch einmal."
        override val creditCostsTitle = "Was Aktionen kosten"
        override fun balance(monthlyLeft: String, purchased: Int) = "Übrig: $monthlyLeft monatlich + $purchased gekauft"
    }

    override val admin = object : AdminStrings {
        override val nav = "🛠 Admin"
        override val pageTitle = "Craftiva AI — Admin"
        override val title = "Admin"
        override val tabOverview = "📊 Übersicht"
        override val tabPlans = "💳 Tarife"
        override val tabPacks = "⚡ Pakete"
        override val tabUsers = "👥 Nutzer"
        override val tabModeration = "🛡️ Moderation"
        override val tabAudit = "📜 Protokoll"
        override val days7 = "7 Tage"
        override val days30 = "30 Tage"
        override val totalCost = "Kosten gesamt"
        override val anonymousCost = "Ohne Anmeldung"
        override val topUsers = "Teuerste Nutzer"
        override val byDay = "Nach Tagen"
        override val events = "Ereignisse"
        override val noData = "Noch keine Daten"
        override val name = "Name"
        override val monthlyCredits = "Credits pro Monat"
        override val credits = "Credits"
        override val price = "Preis (Cent)"
        override val currency = "Währung"
        override val paddlePriceId = "Paddle Price ID"
        override val active = "Aktiv"
        override val unlimitedHint = "Leer = unbegrenzt"
        override val save = "Speichern"
        override val saved = "Gespeichert"
        override val priceWarning = "Der abgebuchte Betrag kommt vom Paddle-Preis. Lege zum Ändern einen neuen Preis in Paddle an und trage dessen Price ID hier ein."
        override val addPack = "Paket hinzufügen"
        override val packId = "Paket-ID (a-z, 0-9, _)"
        override val searchUsers = "Nach E-Mail suchen…"
        override val email = "E-Mail"
        override val plan = "Tarif"
        override val creditsMonth = "Credits diesen Monat"
        override val purchased = "Gekauft"
        override val cost30d = "$ in 30 Tagen"
        override val status = "Status"
        override val registered = "Registriert"
        override val lastLogin = "Letzte Anmeldung"
        override val creditsDelta = "Credits (+ gutschreiben / − abziehen)"
        override val note = "Notiz"
        override val grant = "Anwenden"
        override val granted = "Credits geändert"
        override val block = "Sperren"
        override val unblock = "Entsperren"
        override val statusChanged = "Status geändert"
        override val noUsers = "Keine Nutzer gefunden"
        override val onlyOpen = "Nur offene"
        override val resolve = "Geprüft"
        override val resolved = "Als geprüft markiert"
        override val kind = "Typ"
        override val category = "Kategorie"
        override val excerpt = "Ausschnitt"
        override val noIncidents = "Keine Vorfälle"
        override val whenLabel = "Wann"
        override val admin = "Wer"
        override val action = "Aktion"
        override val target = "Objekt"
        override val day = "Tag"
        override val user = "Nutzer"
        override val cost = "Kosten"
        override val providers = "Anbieter"
        override val details = "Details"
        override val totalEvents = "Ereignisse gesamt"
        override fun saveFailed(reason: String) = "Speichern fehlgeschlagen: $reason"
    }

    override val panel = object : AssetPanelStrings {
        override val tabCode = "Code"
        override val tabChat = "KI-Chat"
        override val tabVersions = "Versionen"
        override val tabRequest = "Anfrage"
        override val tabFiles = "Dateien"
        override val tab3d = "3D"
        override val tabPrep = "Vorbereitungsskript"
        override val tabSketch = "Skizze"
        override val tabAnimation = "Animation"
        override val downloadGlb = ".glb herunterladen"
        override val downloadLua = ".lua herunterladen"
        override val validationOk = "Geprüft: keine Fehler"
        override val technical = "Technische Details"
        override val whatToChange = "Was soll sich ändern? Z. B.: Tür nach 3 Sekunden schließen"
        override val change = "Ändern"
        override val currentVersion = "Aktuell"
        override val references = "Beispielbilder"
        override val noVersions = "Bisher nur eine Version. Neue entstehen durch Änderungen im Chat."
        override fun version(n: Int) = "Version $n"
        override fun validationIssues(n: Int) = "Hinweise: $n"
    }

    override val tour = object : TourStrings {
        override val step1Title = "Beschreib deine Idee"
        override val step1Text = "Schreib, was du brauchst: ein Skript, ein 3D-Modell oder eine Animation. Mit der Büroklammer kannst du ein Beispielbild anhängen."
        override val step2Title = "Hol dir das Ergebnis"
        override val step2Text = "Die KI erstellt es in wenigen Sekunden. Öffne es, um Code oder Modell anzusehen und Änderungen zu wünschen."
        override val step3Title = "Schick es an Roblox Studio"
        override val step3Text = "Verbinde das Plugin in den Einstellungen — dann landen fertige Werke mit einem Klick in Studio."
        override val next = "Weiter"
        override val back = "Zurück"
        override val start = "Los geht’s"
        override val skip = "Überspringen"
        override fun stepOf(step: Int, total: Int) = "Schritt $step von $total"
    }

    override val legal = object : LegalStrings {
        override val terms = "Nutzungsbedingungen"
        override val privacy = "Datenschutz"
        override val refunds = "Rückerstattung"
        override val notAffiliated = "Nicht mit der Roblox Corporation verbunden. Roblox ist eine Marke der Roblox Corporation."
        override val loginConsent = "Mit der Anmeldung akzeptierst du die Nutzungsbedingungen und die Datenschutzerklärung. Die Nutzung ist ab 13 Jahren erlaubt."
        override val checkoutConsent = "Käufe nur für Erwachsene (18+). Die Zahlung wickelt Paddle ab. Mit der Zahlung akzeptierst du die Nutzungsbedingungen und die Rückerstattungsrichtlinie."
        override val backToSite = "← Zur Website"
    }

    override val access = object : AccessStrings {
        override val badge = "Geschlossene Beta"
        override val title = "Erstelle Roblox-Spiele mit KI"
        override val subtitle = "Beschreibe deine Idee in Worten — Craftiva AI schreibt ein Luau-Skript, erstellt ein 3D-Modell oder eine Animation und schickt alles in Roblox Studio. Wir testen den Dienst gerade mit einer kleinen Gruppe."
        override val f1Title = "Luau-Skripte"
        override val f1Text = "Türen, Waffen, Shops, Schadenssysteme — fertiger, geprüfter Code nach deiner Beschreibung."
        override val f2Title = "3D-Modelle"
        override val f2Text = "Zeichne ein Konzept oder beschreibe einen Gegenstand — du bekommst ein Roblox-fertiges Modell."
        override val f3Title = "Direkt in Studio"
        override val f3Text = "Das Craftiva-Plugin bringt das Ergebnis mit einem Klick in dein Spiel."
        override val formTitle = "Zugang anfragen"
        override val formHint = "Hinterlasse eine Anfrage — wir schalten dich frei und schicken dir eine E-Mail."
        override val nameLabel = "Dein Name"
        override val namePlaceholder = "Name oder Nickname"
        override val messageLabel = "Was möchtest du bauen"
        override val messagePlaceholder = "Zum Beispiel: mein eigenes Obby mit Fallen und Shop"
        override val ageConfirm = "Ich bin 13 oder älter (unter 16 und in der EU — mit Zustimmung der Eltern)"
        override val privacyAccept = "Ich akzeptiere die Datenschutzerklärung"
        override val submit = "Anfrage senden"
        override val sentTitle = "Anfrage gesendet!"
        override fun sentText(email: String) = "Wir schreiben an $email, sobald der Zugang frei ist. Meist dauert das 1–2 Tage."
        override val alreadyApproved = "Schon freigeschaltet?"
        override val signIn = "Anmelden"
        override val notApproved = "Für diese E-Mail ist noch kein Zugang frei. Hinterlasse eine Anfrage auf der Startseite — wir schreiben dir nach der Freigabe."
        override val consentRequired = "Bestätige dein Alter und die Datenschutzerklärung"
        override val sendFailed = "Die Anfrage konnte nicht gesendet werden. Versuch es noch einmal."
        override val adminTab = "Anfragen"
        override val pending = "Offen"
        override val approved = "Freigegeben"
        override val rejected = "Abgelehnt"
        override val all = "Alle"
        override val approve = "Freigeben"
        override val reject = "Ablehnen"
        override val noRequests = "Keine Anfragen"
        override val approvedToast = "Zugang freigegeben, E-Mail gesendet"
        override val rejectedToast = "Anfrage abgelehnt"
    }

    override val meshInspector = object : MeshInspectorStrings {
        override val reliefMap = "Reliefkarte"
        override val reliefYes = "ja — feine Details im Licht"
        override val reliefNo = "nein"
        override val relief = "Relief"
        override val reliefHint = "Feine Details des detaillierten Modells, in einer Textur gespeichert (so zeigt Roblox sie)"
        override fun number(value: Int) = groupDigits(value, ".")
        override val tabInfo = "Info"
        override val tabView = "Ansicht"
        override val tabTools = "Werkzeuge"
        override fun studs(value: Double): String {
            val n = shortDecimal(value, comma = true)
            return if (n == "1") "1 Stud" else "$n Studs"
        }
        override val readinessTitle = "Bereit für Roblox?"
        override val readyAll = "Bereit für Roblox"
        override fun readyIssues(count: Int) = if (count == 1) "1 Punkt zu verbessern" else "$count Punkte zu verbessern"
        override fun checkTriangles(count: Int, max: Int) = "Dreiecke: ${groupDigits(count, ".")} von ${groupDigits(max, ".")}"
        override val checkTrianglesOver = "Über dem MeshPart-Limit — Studio lehnt das Modell ab"
        override fun checkTexture(size: Int) = "Textur ${size}×$size"
        override val checkTextureNone = "Keine Textur — die Farben stecken in den Eckpunkten"
        override val checkTextureOver = "Roblox verkleinert die Textur auf 1024 — besser jetzt verkleinern"
        override val checkFrontAuto = "Die Vorderseite wurde automatisch zur Roblox-Front (−Z) gedreht"
        override val checkFrontUser = "Du hast die Vorderseite gewählt"
        override val checkFrontUnsure = "Unklar, wo vorne ist — sieh dir die Ansicht „Vorne“ an"
        override fun checkSize(dimensions: String) = "Größe: $dimensions"
        override val checkSizeTiny = "Sehr klein — im Spiel schwer zu sehen"
        override val checkSizeHuge = "Sehr groß — prüfe, ob das so gewollt ist"
        override val checkPartsOk = "Ein festes Modell ohne lose Teile"
        override fun checkParts(small: Int) = "Lose Teile: $small — sieht nach Generierungsresten aus"
        override val checkPivotOk = "Steht auf dem Boden, Drehpunkt unten"
        override val checkPivotOff = "Steht nicht auf dem Boden — in Studio schwebt oder versinkt es"
        override val checking = "Wird geprüft…"
        override val fixSimplify = "Vereinfachen"
        override val fixTexture = "Verkleinern"
        override val fixRotate = "Drehen"
        override val fixSize = "Größe festlegen"
        override val fixDebris = "Reste entfernen"
        override val fixPivot = "Auf den Boden stellen"
        override val geometry = "Geometrie"
        override val triangles = "Dreiecke"
        override val vertices = "Eckpunkte"
        override val parts = "Einzelteile"
        override val size = "Größe"
        override val fileSize = "Datei"
        override fun megabytes(bytes: Long) = if (bytes < 1024 * 1024) "${shortDecimal(bytes / 1024.0, true)} KB" else "${shortDecimal(bytes / 1048576.0, true)} MB"
        override val texture = "Textur"
        override fun pixels(size: Int) = "${size}×$size px"
        override val noTexture = "keine"
        override val origin = "Herkunft"
        override val provider = "Generator"
        override val created = "Erstellt"
        override val version = "Version"
        override val sketch = "Skizze"
        override val mode = "Modus"
        override val light = "Licht"
        override val background = "Hintergrund"
        override val bgDark = "Dunkel"
        override val bgLight = "Hell"
        override val bgGrey = "Grau"
        override val bgBaseplate = "Baseplate"
        override val reference = "R15-Avatar daneben"
        override val referenceHint = "Vergleiche das Modell mit einer normalen Roblox-Figur (5,2 Studs)"
        override val bounds = "Maße in Studs"
        override val grid = "Bodenraster"
        override val spin = "Drehen"
        override val screenshot = "Screenshot"
        override val screenshotTransparent = "Ohne Hintergrund (PNG)"
        override val video = "Dreh-Video"
        override val videoRecording = "Video wird aufgenommen…"
        override val videoUnsupported = "Dieser Browser kann kein Video aufnehmen"
        override val toolsNote = "Jede Änderung wird als neue Version gespeichert — du kannst immer zurück."
        override val budget = "Anzahl der Dreiecke"
        override val budgetMobile = "Handys"
        override val budgetNormal = "Normal"
        override val budgetDetailed = "Detailliert"
        override fun trianglesValue(count: Int) = "${groupDigits(count, ".")} Dreiecke"
        override val apply = "Anwenden"
        override val sizeTitle = "Größe in Studs"
        override val sizeHint = "Die längste Seite des Modells"
        override val rotateTitle = "Vorderseite drehen"
        override val rotateHint = "Wenn das Modell in Studio seitlich oder rückwärts steht"
        override val rotateLeft = "↺ 90°"
        override val rotateRight = "↻ 90°"
        override val rotate180 = "180°"
        override val textureTitle = "Texturgröße"
        override val debrisTitle = "Lose Teile entfernen"
        override val debrisHint = "Kleine schwebende Stücke, die der Generator übrig gelassen hat"
        override val downloadTitle = "Herunterladen"
        override val downloadGlb = "GLB"
        override val downloadObj = "OBJ + Textur"
        override val versions = "Versionen"
        override fun versionLabel(number: Int) = "v$number"
        override val current = "aktuell"
        override val restore = "Zurückholen"
        override val compare = "Vergleichen"
        override val closeCompare = "Vergleich schließen"
        override val originalVersion = "Wie generiert"
        override fun editTriangles(count: Int) = "Netz mit ${groupDigits(count, ".")} Dreiecken"
        override val editHighPoly = "detailliertes Modell (High-Poly)"
        override fun budgetFromSource(sourceTriangles: Int?) = "Das detaillierte Modell des Generators${sourceTriangles?.let { " (" + groupDigits(it, ".") + " Dreiecke)" } ?: ""} wird 30 Tage nach dem Erstellen aufbewahrt: Die Dreieckszahl kann sinken und steigen."
        override val downloadHighPoly = "High-Poly (GLB)"
        override val downloadSharp = "GLB · 2K-Textur"
        override val rigTitle = "Skelett für Animationen"
        override val rigHint = "Setzt 15 Knochen wie bei R15 ins Modell (kostenlos). Am besten für aufrecht stehende Figuren. Danach das Modell in den Animationseinstellungen als Figur wählen."
        override val rigApply = "Knochen einsetzen"
        override val rigDone = "Knochen eingesetzt: das Modell kann als Animationsfigur gewählt werden."
        override val rigDownload = "GLB mit Skelett"
        override val rigWorking = "Setze Knochen ein…"
        override fun rigPro(cost: String?) = "Genauer — Meshy" + (cost?.let { " · $it" } ?: "")
        override val rigProHint = "Meshy setzt 24 Knochen und verteilt die Haut sauberer; nur für Menschenfiguren, etwa eine Minute."
        override val rigByMeshy = "Knochen von Meshy"
        override val preparingSharp = "Das Modell mit 2K-Textur wird vorbereitet, das dauert bis zu einer Minute."
        override val retextureTitle = "Textur ändern"
        override val retextureHint = "Form und Größe bleiben, Farben und Material ändern sich. Dauert 1–3 Minuten und ergibt eine neue Version. Beschreibe den Stil oder füge ein Stilbild hinzu."
        override val retexturePresets = listOf("Gold", "Stein", "Holz", "Rostiges Metall", "Cartoon, kräftige Farben", "Eis")
        override val retexturePlaceholder = "Zum Beispiel: goldene Ritterrüstung mit roten Details"
        override val retexturePicture = "Stilbild"
        override val retextureRemovePicture = "Bild entfernen"
        override fun retextureApply(cost: String?) = "Neu bemalen" + (cost?.let { " · $it" } ?: "")
        override val retextureWorking = "Das Modell wird neu bemalt, das dauert 1–3 Minuten"
        override val editRetexture = "neue Textur"
        override fun editSize(studs: String) = "Größe $studs"
        override fun editRotate(degrees: Int) = "um $degrees° gedreht"
        override fun editTexture(size: Int) = "Textur $size px"
        override val editDebris = "Reste entfernt"
        override val working = "Das Modell wird geändert… das kann bis zu einer Minute dauern"
        override fun saved(number: Int) = "Fertig — als Version $number gespeichert"
        override val failed = "Das Modell konnte nicht geändert werden. Versuch es noch einmal."
        override val unavailable = "Die Modell-Werkzeuge sind auf diesem Server noch nicht verbunden"
    }

    override val animationSetup = object : AnimationSetupStrings {
        override val characterTitle = "Figur"
        override val characterR15 = "Standard-R15"
        override val characterR15Hint = "der übliche Roblox-Avatar"
        override val characterCustom = "Eigene Figur"
        override val characterCustomHint = "Eigene Figur: 3D-Arbeit öffnen → Werkzeuge → Knochen einsetzen."
        override val characterOwnHint = "meine 3D-Arbeit mit Knochen"
        override val modeTitle = "Modus"
        override val modeNone = "Normal"
        override val modeNoneHint = "Tänze, Gehen, Emotes"
        override val modeFists = "Fäuste"
        override val modeFistsHint = "Schläge, Blocks, Ausweichen"
        override val modeOne = "Eine Waffe"
        override val modeOneHint = "Schwert, Waffe, Stab, Axt"
        override val modeTwo = "Zwei Waffen"
        override val modeTwoHint = "Kombos mit beiden Händen"
        override val weaponTitle = "Waffe (rechte Hand)"
        override val offhandTitle = "Linke Hand"
        override val sword = "Schwert"
        override val gun = "Schusswaffe"
        override val staff = "Stab"
        override val axe = "Axt"
        override fun weaponHint(weapon: String) = when (weapon) { "GUN" -> "Zielen und Rückstoß"; "STAFF" -> "weite Schwünge"; "AXE" -> "schwer, mit Ausholen"; else -> "schnelle Hiebe" }
        override val weaponModelTitle = "Waffenmodell"
        override val weaponModelBuiltIn = "Eingebaut"
        override val weaponModelBuiltInHint = "einfache Form"
        override val weaponModelOwnHint = "meine 3D-Arbeit"
        override val gripTitle = "Griff"
        override val gripOne = "Eine Hand"
        override val gripOneHint = "die andere Hand ist frei"
        override val gripTwo = "Zwei Hände"
        override val gripTwoHint = "beide Hände am Griff"
        override val note = "Die Bewegungen folgen dem Gewicht der Waffe: schwere holen aus und schwingen nach. Die Waffe selbst erscheint später in der Vorschau."
        override val noteWithWeapon = "Die Bewegungen folgen dem Gewicht der Waffe: schwere holen aus und schwingen nach. Die Vorschau zeigt die Waffe in der Hand; in Studio befestigt das Plugin einen Platzhalter an der Hand."
        override val openWorkspace = "Arbeitsbereich"
        override val closeWorkspace = "Schließen"
        override val clipTitle = "Clip"
        override val duration = "Länge"
        override val looped = "in Schleife"
        override val once = "einmal"
        override val keys = "Keys"
        override val jointsTitle = "Gelenke jetzt"
        override val prevKey = "Vorheriger Key"
        override val nextKey = "Nächster Key"
        override val zoomIn = "Heranzoomen"
        override val zoomOut = "Herauszoomen"
        override val resetCamera = "Kamera zurücksetzen"
        override val timeZoom = "Zeitmaßstab"
        override val cameraHint = "Rad zum Zoomen · linke Taste dreht · rechte Taste oder Shift verschiebt"
        override val head = "Kopf"
        override val torso = "Rumpf"
        override val leftArm = "Linker Arm"
        override val rightArm = "Rechter Arm"
        override val leftLeg = "Linkes Bein"
        override val rightLeg = "Rechtes Bein"
        override val editTitle = "Animation ändern"
        override val editHint = "Beschreibe, was sich ändern soll, der Rest bleibt. Jede Änderung wird als Version gespeichert."
        override val editPresets = listOf("Weitere Armbewegungen", "Schneller", "Langsamer", "Weicher", "Höherer Sprung", "Schneller zur Ausgangspose")
        override val editPlaceholder = "Zum Beispiel: Schwerthieb schärfer und mit mehr Ausholen"
        override fun editApply(cost: String?) = "Ändern" + (cost?.let { " · $it" } ?: "")
        override val editWorking = "Animation wird geändert…"
        override fun editDone(version: Int) = "Fertig: Version $version"
        override val editFailed = "Ändern hat nicht geklappt"
        override val versionsTitle = "Versionen"
        override val versionOriginal = "Original"
        override val restore = "Zurück"
        override val libraryOpen = "Modell wählen…"
        override val libraryTitle = "Charakter- und Waffenmodell"
        override val libraryDone = "Fertig"
        override val libraryCharacterHint = "Ein Charakter braucht Knochen — ein hochgeladenes Modell bekommt sie automatisch. Unten kannst du dein eigenes Modell hochladen."
        override val libraryWeaponHint = "Das Modell, das der Charakter hält. Eingebaut ist Schwert, Pistole, Stab oder Axt des Modus."
        override val libraryNoBones = "ohne Knochen"
        override val libraryEmpty = "Hier erscheinen deine 3D-Werke und hochgeladenen Modelle."
        override val libraryAddBones = "Knochen setzen (kostenlos)"
        override val libraryAddingBones = "Setze Knochen…"
        override val libraryTools = "Modell-Werkzeuge"
        override val libraryDownload = ".glb herunterladen"
        override val libraryUpload = "Eigenes Modell hochladen"
        override val libraryUploadHint = ".glb oder .obj mit .mtl und Texturen, bis 30 MB. Klicke oder ziehe die Dateien hierher."
        override val libraryUploading = "Lade und bereite das Modell vor…"
        override val libraryFailed = "Das hat nicht geklappt"
        override fun libraryImported(name: String) = "Modell „$name“ hochgeladen"
        override val weaponTitleOne = "Waffe — eingebaut oder aus „Meine Werke“"
        override val rightHandTitle = "Rechte Hand"
        override val leftHandTitle = "Linke Hand"
        override val handTitle = "Hand"
        override val handLeadTitle = "Führende Hand"
        override val handRight = "Rechts"
        override val handLeft = "Links"
        override val handRightHint = "Die Waffe in der rechten Hand"
        override val handLeftHint = "Die Waffe in der linken Hand: die Bewegungen werden gespiegelt"
        override fun weaponMovesLike(kind: String) = "bewegt sich wie ${kind.lowercase()}"
    }

    override val quality = object : QualityStrings {
        override val sketchTitle = "Skizze"
        override val meshTitle = "3D-Modell"
        override val sketchStandard = "Schnell"
        override val sketchStandardHint = "günstig, malt aber manchmal Extras dazu"
        override val sketchHigh = "Genau"
        override val sketchHighHint = "hält sich viel besser an die Beschreibung"
        override val meshStandard = "Normal"
        override val meshStandardHint = "≈30 s, zum Ausprobieren und für einfache Dinge"
        override val meshHigh = "Hoch"
        override val meshHighHint = "≈1 Min., sauberere Form und Textur"
        override val meshMax = "Maximal"
        override val meshMaxHint = "≈3 Min., die meisten Details"
        override val note = "Höhere Qualität kostet mehr Credits. Wähle passend zur Aufgabe — zum Ausprobieren reicht Normal."
        override fun approve(cost: String) = "3D erstellen · $cost"
        override val drewCheaper = "Das genaue Skizzenmodell ist gerade nicht verfügbar — das schnelle hat gezeichnet, berechnet wurde der günstigere Preis"
        override val variantsTitle = "Wie viele Skizzen"
        override val variantsOne = "1"
        override val variantsOneHint = "wenn die Idee schon klar ist"
        override val variantsFour = "4 zur Auswahl"
        override val variantsFourHint = "auf einmal gezeichnet, du wählst die beste"
        override val pickVariant = "Klicke auf die Skizze, aus der das 3D-Modell entstehen soll"
        override fun variantLabel(number: Int) = "Variante $number"
        override val trianglesTitle = "Polygone (Dreiecke)"
        override val trianglesMob = "Mob / Deko"
        override val trianglesCharacter = "Spieler / Figur"
        override val trianglesBoss = "Boss / großes Objekt"
        override val trianglesHighPoly = "High-Poly"
        override val trianglesHighPolyHint = "ohne Limit, zum Herunterladen; Roblox nimmt bis 20.000"
        override val trianglesCustom = "Eigene Zahl"
        override fun trianglesUpTo(count: Int) = "bis ${groupDigits(count, ".")}"
        override val trianglesNote = "Ein Roblox-MeshPart nimmt bis zu 20.000 Dreiecke. Mehr Dreiecke behalten mehr kleine Details, machen das Modell aber schwerer fürs Spiel."
        override val textureTitle = "Textur"
        override val texture1kHint = "1024 px, wie in Roblox"
        override val texture2kHint = "2048 px, schärfer im heruntergeladenen Modell"
        override val texture4kHint = "4096 px, bei Hoch, im High-Poly-Modell"
        override val textureNote = "Roblox bekommt immer eine 1024-px-Textur: Studio verkleinert größere. 2K und 4K sind für Downloads und andere Programme."
    }

    override val accountData = object : AccountDataStrings {
        override val title = "Deine Daten"
        override val hint = "Lade alles herunter, was der Dienst über dich speichert, oder lösche dein Konto endgültig."
        override val download = "Meine Daten herunterladen"
        override val delete = "Konto löschen"
        override val deleteTitle = "Konto endgültig löschen?"
        override val deleteWarning = "Dein Konto, alle deine Arbeiten, ihre Versionen, 3D-Modelle und Skizzen werden gelöscht. Unbenutzte Credits verfallen. Das lässt sich nicht rückgängig machen."
        override val confirmLabel = "Gib zur Bestätigung deine E-Mail ein"
        override val confirmButton = "Endgültig löschen"
        override val emailMismatch = "Die E-Mail passt nicht zu diesem Konto"
        override val subscriptionActive = "Kündige zuerst dein Abo auf der Preisseite, dann lösche das Konto."
        override val deleted = "Konto gelöscht"
        override val failed = "Das Konto konnte nicht gelöscht werden. Bitte versuche es erneut."
        override val adminDelete = "Konto löschen"
        override val adminDeleted = "Konto gelöscht"
        override fun adminConfirm(email: String) = "Konto $email mit allen Arbeiten löschen? Das lässt sich nicht rückgängig machen."
    }
}
